import { CommonModule } from '@angular/common';
import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { FeatureFlagService } from '@services/helpers/feature-flag.service';
import { TokenService } from '@services/helpers/token.service';
import { TooltipModule } from 'primeng/tooltip';
import { filter } from 'rxjs';
import { CHAT_FEATURE_FLAG } from '../../models/chat.models';
import { ChatStore } from '../../services/chat.store';
import { isChatUrl } from '../../utils/chat-route.util';

/**
 * The chat entry point in the top bar, sitting with the notification bell and notes.
 *
 * Only the button lives here. The panel it opens, the toast and the dialogs stay mounted in the
 * app shell ({@link ChatLauncherComponent}) rather than moving into the header with it: the top
 * bar is `position: fixed` with a `z-index`, which makes it a stacking context, and a panel
 * rendered inside one cannot lift above the rest of the page no matter how high its own z-index
 * is. Both read the same root {@link ChatStore}, so the button and the panel cannot disagree.
 *
 * Unread messages are announced twice over — a count, and a pulse on the ring around it. The count
 * alone is easy to miss in a bar that already has a badge on the bell.
 */
@Component({
  selector: 'app-chat-trigger',
  standalone: true,
  imports: [CommonModule, TooltipModule],
  template: `
    <button
      #btn
      *ngIf="enabled && !onChatPage"
      type="button"
      class="chat-trigger"
      [class.has-alert]="unread > 0"
      [class.is-open]="store.panelOpen()"
      (click)="toggle()"
      [attr.aria-label]="unread > 0 ? unread + ' unread chat messages' : 'Chat'"
      [pTooltip]="'Chat'"
      tooltipPosition="bottom">
      <!-- <i class="pi pi-comments _fw-400"></i> -->
      <img src="assets/img/dashboadIcon/commentIcon.svg" alt="" />
      <span class="chat-trigger__badge" *ngIf="unread > 0">{{ unread > 99 ? '99+' : unread }}</span>
    </button>
  `,
  styleUrls: ['./chat-trigger.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatTriggerComponent implements OnInit, AfterViewChecked {
  readonly store = inject(ChatStore);
  private readonly flags = inject(FeatureFlagService);
  private readonly tokens = inject(TokenService);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild('btn') private btnRef?: ElementRef<HTMLButtonElement>;

  enabled = false;

  /**
   * Hidden on the full chat page, where the button would only reopen what you are already reading.
   *
   * Kept in step with the router as a field rather than read from `router.url` in a getter: this
   * component is OnPush and lives in the shell, so it is never re-created and a navigation on its
   * own does not mark it dirty.
   */
  onChatPage = false;

  ngOnInit(): void {
    this.enabled = this.flags.isFeatureEnabled(CHAT_FEATURE_FLAG) && this.tokens.isUserloggedIn();
    this.onChatPage = isChatUrl(this.router.url);

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(event => {
        const next = isChatUrl(event.urlAfterRedirects || event.url);
        if (next !== this.onChatPage) {
          this.onChatPage = next;
          this.cdr.markForCheck();
        }
      });

    // No `store.init()` here — the launcher in the app shell already loads the sidebar, and a
    // second call would fetch it twice on every page load.
  }

  get unread(): number {
    return this.store.totalUnread();
  }

  toggle(): void {
    this.store.togglePanel();
  }

  /**
   * Keeps {@link ChatStore.triggerAnchor} in step with where this button actually is, so the toast
   * stack (mounted separately in the app shell) can drop in under it. Runs every checked cycle
   * rather than once on init — the button's position shifts with things this component has no other
   * hook into (profile name length, sidebar collapse, window resize) — but only writes to the store
   * when the measured position has actually changed, so it settles after one pass.
   */
  ngAfterViewChecked(): void {
    const el = this.btnRef?.nativeElement;
    if (!el) {
      if (this.store.triggerAnchor()) {
        this.store.triggerAnchor.set(null);
      }
      return;
    }

    const rect = el.getBoundingClientRect();
    const next = { top: Math.round(rect.bottom), right: Math.round(window.innerWidth - rect.right) };
    const prev = this.store.triggerAnchor();
    if (!prev || prev.top !== next.top || prev.right !== next.right) {
      this.store.triggerAnchor.set(next);
    }
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    // OnPush: a bare resize does not otherwise mark this component dirty, so `ngAfterViewChecked`
    // above would never re-run to pick up the button's new position.
    this.cdr.markForCheck();
  }
}
